import {
    ActionViewAdditionalWhereFilter,
    ActionViewOutputColumn,
    ActionViewPickerItem,
    ActionViewSavedCriteria,
    ActionViewSearchRequest,
    ActionViewTeamSelection,
    ActionViewUserTeamSelection,
    TaskActionViewCompletedOption,
    TaskActionViewDateTimeOption,
    TaskActionViewIncludeOption,
    TaskActionViewOrganizeBy,
    TaskActionViewShowOption,
    TaskActionViewThatAreOption,
    TaskActionViewUrgencyOption,
    TaskActionViewWhereOption,
} from './actionView.models';

export interface ActionViewColumnIdentity {
    id?: number;
    columnId?: number;
    advancedSearchFieldID?: number;
}

export interface ActionViewFilterOperator {
    name: string;
}

export interface ActionViewFilterElement {
    operator: ActionViewFilterOperator | string;
    value: string;
}

export interface ActionViewFilterColumn extends ActionViewColumnIdentity {
    or?: ActionViewFilterElement[];
}

type ActionViewOutputColumnSource = ActionViewColumnIdentity &
    Partial<ActionViewOutputColumn> & {
        resultsColumnName?: string;
    };

type UnknownRecord = Record<string, unknown>;

const LEGACY_PROPERTY_NAMES: Readonly<Record<keyof ActionViewSearchRequest, string>> = {
    taskSeriesTypeID: 'taskSeriesTypeID',
    taskTypeID: 'taskTypeID',
    taskTypeName: 'taskTypeName',
    showChoice: 'ShowChoice',
    whereChoice: 'WhereChoice',
    thatAreChoice: 'ThatAreChoice',
    includeChoice: 'IncludeChoice',
    showUserTeams: 'ShowUserTeams',
    showTeams: 'ShowTeams',
    includeUserTeams: 'IncludeUserTeams',
    includeTeams: 'IncludeTeams',
    additionalWhereFilters: 'AdditionalWhereFilters',
    dateTimeChoice: 'DateTimeChoice',
    dueWithinDays: 'DueWithinDays',
    useUrgencyFilter: 'UseUrgencyFilter',
    urgencyChoice: 'UrgencyChoice',
    includeUnscheduled: 'IncludeUnscheduled',
    onlyUnscheduled: 'OnlyUnscheduled',
    includeSkipped: 'includeSkipped',
    onlySkipped: 'onlySkipped',
    dueDateFrom: 'DueDateFrom',
    dueDateTo: 'DueDateTo',
    useLastActivityFilter: 'UseLastActivityFilter',
    lastActivityFrom: 'LastActivityFrom',
    lastActivityTo: 'LastActivityTo',
    lastActivityTimeSpan: 'LastActivityTimeSpan',
    completedDateFrom: 'CompletedDateFrom',
    completedDateTo: 'CompletedDateTo',
    completedWithinDays: 'CompletedWithinDays',
    completedChoice: 'CompletedChoice',
    filters: 'filters',
    entityRelatedFilters: 'EntityRelatedFilters',
    taskOutputColumnIds: 'TaskOutputColumnIds',
    entityOutputColumnIds: 'EntityOutputColumnIds',
    addedTaskOutputColumnIds: 'AddedTaskOutputColumnIds',
    addedEntityOutputColumnIds: 'AddedEntityOutputColumnIds',
    taskOutputColumns: 'TaskOutputColumns',
    entityOutputColumns: 'EntityOutputColumns',
    customOutput: 'CustomOutput',
    output: 'output',
    detailedResults: 'DetailedResults',
    organizeBy: 'OrganizeBy',
    groupBy: 'GroupBy',
    addedGroupBy: 'AddedGroupBy',
    calculateReadyAndNotReady: 'CalculateReadyAndNotReady',
    showMostPressingTasks: 'ShowMostPressingTasks',
    outputType: 'outputType',
    outputInt: 'outputInt',
    justReturnError: 'JustReturnError',
    searchName: 'searchName',
    mostPressingNotReadyOnly: 'MostPressingNotReadyOnly',
    persistCustomColumns: 'persistCustomColumns',
    showTotals: 'showTotals',
    showOrs: 'showOrs',
    dontUseAsync: 'dontUseAsync',
    executedByUserID: 'executedByUserID',
    instanceIds: 'instanceIds',
    limitNumberOfResults50k: 'limitNumberOfResults50k',
    privateAccessInstanceIds: 'privateAccessInstanceIds',
    savedSearchID: 'savedSearchID',
    sortByAllFieldsIncludingInvisibleOnes: 'sortByAllFieldsIncludingInvisibleOnes',
};

export const ACTION_VIEW_SEARCH_TIMEOUT_TITLE: string = 'Action View search timed out';
export const ACTION_VIEW_SEARCH_TIMEOUT_MESSAGE: string =
    'The search took too long to complete. Try narrowing the filters.';

const GATEWAY_TIMEOUT_STATUS: number = 504;

export function isActionViewSearchTimeout(error: unknown): boolean {
    return typeof error === 'object' && error !== null && (error as UnknownRecord)['status'] === GATEWAY_TIMEOUT_STATUS;
}

export function getActionViewFieldId(column: ActionViewColumnIdentity): number {
    const fieldId = [column.id, column.columnId, column.advancedSearchFieldID].find(
        (candidate) => Number.isInteger(candidate) && Number(candidate) > 0,
    );
    if (fieldId === undefined) {
        throw new Error('Action View column is missing a valid field identifier.');
    }
    return fieldId;
}

/**
 * Carries the filter values a user already entered onto freshly built columns, matched by field id.
 * Columns that had no previous filter keep their own values.
 */
export function reuseActionViewFilterValues<T extends ActionViewColumnIdentity & { or?: unknown[] }>(
    columns: T[],
    previousColumns: T[],
): T[] {
    const previousOrById = new Map(previousColumns.map((column) => [getActionViewFieldId(column), column.or]));
    return columns.map((column) => {
        const previousOr = previousOrById.get(getActionViewFieldId(column));
        return previousOr ? { ...column, or: structuredClone(previousOr) } : column;
    });
}

export function toActionViewOutputColumn(column: ActionViewOutputColumnSource): ActionViewOutputColumn {
    return {
        id: getActionViewFieldId(column),
        sequence: toNumber(column.sequence, 0),
        displayName: toOptionalString(column.displayName),
        depthValue: toOptionalNumber(column.depthValue),
        resultColumnName: column.resultColumnName ?? column.resultsColumnName,
        canGroupTaskActionView: toOptionalBoolean(column.canGroupTaskActionView),
        fromTaskType: toOptionalBoolean(column.fromTaskType),
    };
}

export function normalizeActionViewRequest(value: unknown): ActionViewSearchRequest {
    const source = toRecord(value);
    const get = (property: keyof ActionViewSearchRequest): unknown =>
        source[property] ?? source[LEGACY_PROPERTY_NAMES[property]];

    return {
        taskSeriesTypeID: toNumber(get('taskSeriesTypeID'), 0),
        taskTypeID: toNumber(get('taskTypeID'), 0),
        taskTypeName: toOptionalString(get('taskTypeName')),
        showChoice: toNumber(get('showChoice'), TaskActionViewShowOption.MyTasks) as TaskActionViewShowOption,
        whereChoice: toNumber(get('whereChoice'), TaskActionViewWhereOption.Assigned) as TaskActionViewWhereOption,
        thatAreChoice: toNumber(get('thatAreChoice'), TaskActionViewThatAreOption.Ready) as TaskActionViewThatAreOption,
        includeChoice: toNumber(
            get('includeChoice'),
            TaskActionViewIncludeOption.AllCompleted,
        ) as TaskActionViewIncludeOption,
        showUserTeams: toPickerItems(get('showUserTeams')),
        showTeams: toPickerItems(get('showTeams')),
        includeUserTeams: toPickerItems(get('includeUserTeams')),
        includeTeams: toPickerItems(get('includeTeams')),
        additionalWhereFilters: toAdditionalWhereFilters(get('additionalWhereFilters')),
        dateTimeChoice: toNumber(
            get('dateTimeChoice'),
            TaskActionViewDateTimeOption.AllDates,
        ) as TaskActionViewDateTimeOption,
        dueWithinDays: toNullableNumber(get('dueWithinDays')),
        useUrgencyFilter: toNullableBoolean(get('useUrgencyFilter')),
        urgencyChoice: toNullableNumber(get('urgencyChoice')) as TaskActionViewUrgencyOption | null,
        includeUnscheduled: toBoolean(get('includeUnscheduled'), false),
        onlyUnscheduled: toNullableBoolean(get('onlyUnscheduled')),
        includeSkipped: toBoolean(get('includeSkipped'), true),
        onlySkipped: toNullableBoolean(get('onlySkipped')),
        dueDateFrom: toNullableDateValue(get('dueDateFrom')),
        dueDateTo: toNullableDateValue(get('dueDateTo')),
        useLastActivityFilter: toNullableBoolean(get('useLastActivityFilter')),
        lastActivityFrom: toNullableNumber(get('lastActivityFrom')),
        lastActivityTo: toNullableNumber(get('lastActivityTo')),
        lastActivityTimeSpan: toNullableString(get('lastActivityTimeSpan')),
        completedDateFrom: toNullableDateValue(get('completedDateFrom')),
        completedDateTo: toNullableDateValue(get('completedDateTo')),
        completedWithinDays: toNullableNumber(get('completedWithinDays')),
        completedChoice: toNumber(
            get('completedChoice'),
            TaskActionViewCompletedOption.DatesProvidedByUser,
        ) as TaskActionViewCompletedOption,
        filters: toAdvancedSearchFilters(get('filters')),
        entityRelatedFilters: toAdvancedSearchFilters(get('entityRelatedFilters')),
        taskOutputColumnIds: toNumberArray(get('taskOutputColumnIds')),
        entityOutputColumnIds: toNumberArray(get('entityOutputColumnIds')),
        addedTaskOutputColumnIds: toNumberArray(get('addedTaskOutputColumnIds')),
        addedEntityOutputColumnIds: toNumberArray(get('addedEntityOutputColumnIds')),
        taskOutputColumns: toOutputColumns(get('taskOutputColumns')),
        entityOutputColumns: toOutputColumns(get('entityOutputColumns')),
        customOutput: toBoolean(get('customOutput'), false),
        output: get('output') === 'custom' ? 'custom' : 'default',
        detailedResults: toBoolean(get('detailedResults'), false),
        organizeBy: toNumber(get('organizeBy'), TaskActionViewOrganizeBy.Urgency) as TaskActionViewOrganizeBy,
        groupBy: toNumberArray(get('groupBy')),
        addedGroupBy: toNumberArray(get('addedGroupBy')),
        calculateReadyAndNotReady: toBoolean(get('calculateReadyAndNotReady'), false),
        showMostPressingTasks: toBoolean(get('showMostPressingTasks'), false),
        outputType: toNumber(get('outputType'), 0) as Core.AdvancedSearchRequestOutputTypes,
        outputInt: toNumber(get('outputInt'), 0),
        justReturnError: toNullableBoolean(get('justReturnError')),
        searchName: toNullableString(get('searchName')),
        mostPressingNotReadyOnly: toNullableBoolean(get('mostPressingNotReadyOnly')),
        persistCustomColumns: toBoolean(get('persistCustomColumns'), false),
        showTotals: toBoolean(get('showTotals'), false),
        showOrs: toBoolean(get('showOrs'), false),
        dontUseAsync: toBoolean(get('dontUseAsync'), false),
        executedByUserID: toOptionalString(get('executedByUserID')),
        instanceIds: toNumberArray(get('instanceIds')),
        limitNumberOfResults50k: toBoolean(get('limitNumberOfResults50k'), true),
        privateAccessInstanceIds: toNumberArray(get('privateAccessInstanceIds')),
        savedSearchID: toNullableNumber(get('savedSearchID')),
        sortByAllFieldsIncludingInvisibleOnes: toBoolean(get('sortByAllFieldsIncludingInvisibleOnes'), false),
    };
}

export function parseActionViewSavedCriteria(value: string | UnknownRecord): ActionViewSavedCriteria {
    const source = parseActionViewSavedCriteriaPayload(value);
    return {
        outputColumns: toNumberArray(source.outputColumns),
        filters: toAdvancedSearchFilters(source.filters),
        masterFilter: normalizeActionViewRequest(source.masterFilter),
    };
}

export function parseActionViewSavedCriteriaPayload(value: string | UnknownRecord): UnknownRecord {
    return toRecord(parsePersistedJson(value));
}

export function serializeActionViewSavedCriteria(criteria: ActionViewSavedCriteria): string {
    return JSON.stringify(toActionViewSavedCriteriaPayload(criteria));
}

export function toActionViewSavedCriteriaPayload(criteria: ActionViewSavedCriteria): UnknownRecord {
    return {
        outputColumns: [...criteria.outputColumns],
        filters: criteria.filters.map((filter) => ({
            columnId: filter.columnId,
            or: filter.or.map((element) => ({
                value: element.value,
                operator: element.operator,
            })),
        })),
        masterFilter: toLegacyActionViewRequest(criteria.masterFilter),
    };
}

export function toLegacyActionViewRequest(request: ActionViewSearchRequest): UnknownRecord {
    const legacy: UnknownRecord = {};
    const source = request as ActionViewSearchRequest & UnknownRecord;
    (Object.keys(LEGACY_PROPERTY_NAMES) as (keyof ActionViewSearchRequest)[]).forEach((property) => {
        const value = source[property];
        if (value !== undefined) {
            legacy[LEGACY_PROPERTY_NAMES[property]] = structuredClone(value);
        }
    });
    return legacy;
}

export function prepareActionViewFilters(columns: ActionViewFilterColumn[]): Core.AdvancedSearchFilter[] {
    return columns
        .map((column) => ({
            columnId: getActionViewFieldId(column),
            or: (column.or ?? [])
                .map((element) => {
                    const operator = getFilterOperatorName(element.operator);
                    return {
                        value: element.value,
                        operator,
                        operatorName: operator,
                    };
                })
                .filter(
                    (element) =>
                        ['blank', 'notBlank', 'isNA'].includes(element.operator) ||
                        hasExecutableFilterValue(element.value),
                ),
        }))
        .filter((filter) => ![9002, 1430].includes(filter.columnId) && filter.or.length > 0);
}

export function prepareActionViewSavedFilters(columns: ActionViewFilterColumn[]): Core.AdvancedSearchFilter[] {
    return columns
        .map((column) => ({
            columnId: getActionViewFieldId(column),
            or: (column.or ?? [])
                .map((element) => {
                    const operator = getFilterOperatorName(element.operator);
                    return {
                        value: element.value,
                        operator,
                        operatorName: operator,
                    };
                })
                .filter((element) => Boolean(element.operator)),
        }))
        .filter((filter) => ![9002, 1430].includes(filter.columnId) && filter.or.length > 0);
}

function toOutputColumns(value: unknown): ActionViewOutputColumn[] {
    return Array.isArray(value)
        ? value.filter(isRecord).map((column) => toActionViewOutputColumn(column as ActionViewOutputColumnSource))
        : [];
}

function toAdvancedSearchFilters(value: unknown): Core.AdvancedSearchFilter[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).map((filter) => ({
        columnId: toNumber(filter.columnId ?? filter.advancedSearchFieldID ?? filter.id, 0),
        or: Array.isArray(filter.or)
            ? filter.or.filter(isRecord).map((element) => {
                  const operator = isRecord(element.operator) ? element.operator.name : element.operator;
                  return {
                      operator: toString(operator),
                      operatorName: toString(element.operatorName ?? operator),
                      value: toString(element.value),
                  };
              })
            : [],
    }));
}

function toPickerItems(value: unknown): ActionViewPickerItem[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).map((selection) => ({
        contactID: toOptionalNumber(selection.contactID ?? selection.contactId),
        teamID: toNullableNumber(selection.teamID ?? selection.teamId),
        userID: toOptionalString(selection.userID ?? selection.userId),
        isDefault: toNullableBoolean(selection.isDefault),
        name: toOptionalString(selection.name),
        ...(isRecord(selection.user)
            ? { user: structuredClone(selection.user) as unknown as ActionViewPickerItem['user'] }
            : {}),
        ...(isRecord(selection.team)
            ? { team: structuredClone(selection.team) as unknown as ActionViewPickerItem['team'] }
            : {}),
    }));
}

function toUserTeams(value: unknown): ActionViewUserTeamSelection[] {
    return toPickerItems(value)
        .filter((selection) => Boolean(selection.userID))
        .map((selection) => ({
            teamID: toNullableNumber(selection.teamID),
            userID: toString(selection.userID),
            isDefault: toNullableBoolean(selection.isDefault),
        }));
}

function toTeams(value: unknown): ActionViewTeamSelection[] {
    return toPickerItems(value)
        .map((selection) => toNullableNumber(selection.teamID))
        .filter((teamID): teamID is number => teamID !== null)
        .map((teamID) => ({ teamID }));
}

function toAdditionalWhereFilters(value: unknown): ActionViewAdditionalWhereFilter[] {
    if (!Array.isArray(value)) {
        return [];
    }
    return value.filter(isRecord).map((filter) => ({
        whereChoice: toNumber(
            filter.whereChoice ?? filter.WhereChoice,
            TaskActionViewWhereOption.Assigned,
        ) as TaskActionViewWhereOption,
        userID: toNullableString(filter.userID ?? filter.UserID),
        userTeams: toUserTeams(filter.userTeams ?? filter.UserTeams),
        teams: toTeams(filter.teams ?? filter.Teams),
    }));
}

function toNumberArray(value: unknown): number[] {
    return Array.isArray(value)
        ? value.map((item) => toNullableNumber(item)).filter((item): item is number => item !== null)
        : [];
}

function toRecord(value: unknown): UnknownRecord {
    return isRecord(value) ? value : {};
}

function parsePersistedJson(value: string | UnknownRecord): unknown {
    let parsed: unknown = value;
    for (let depth = 0; typeof parsed === 'string' && depth < 5; depth++) {
        parsed = JSON.parse(parsed);
    }
    if (typeof parsed === 'string') {
        throw new Error('Saved Action View criteria is not a JSON object.');
    }
    return parsed;
}

function isRecord(value: unknown): value is UnknownRecord {
    return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function getFilterOperatorName(operator: ActionViewFilterElement['operator']): string {
    return typeof operator === 'string' ? operator : operator.name;
}

function hasExecutableFilterValue(value: unknown): boolean {
    if (value === null || value === undefined) {
        return false;
    }
    return typeof value === 'string' ? Boolean(value.trim()) : value instanceof Date || value !== '';
}

function toNumber(value: unknown, fallback: number): number {
    const result = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(result) ? result : fallback;
}

function toOptionalNumber(value: unknown): number | undefined {
    return value === undefined ? undefined : toNumber(value, 0);
}

function toNullableNumber(value: unknown): number | null {
    return value === null || value === undefined || value === '' ? null : toNumber(value, 0);
}

function toBoolean(value: unknown, fallback: boolean): boolean {
    return typeof value === 'boolean' ? value : fallback;
}

function toOptionalBoolean(value: unknown): boolean | undefined {
    return typeof value === 'boolean' ? value : undefined;
}

function toNullableBoolean(value: unknown): boolean | null {
    return typeof value === 'boolean' ? value : null;
}

function toString(value: unknown): string {
    return typeof value === 'string' ? value : value === null || value === undefined ? '' : String(value);
}

function toOptionalString(value: unknown): string | undefined {
    return value === undefined ? undefined : toString(value);
}

function toNullableString(value: unknown): string | null {
    return value === null || value === undefined ? null : toString(value);
}

function toNullableDateValue(value: unknown): Date | string | null {
    return value instanceof Date || typeof value === 'string' ? value : null;
}
